import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, MapPin, Users, CreditCard, Plus, Image, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EventsPage({ user }) {
  const [events, setEvents] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [eventImage, setEventImage] = useState(null);
  const [eventImagePreview, setEventImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    end_time: '',
    location: '',
    is_paid: false,
    price: '',
    max_attendees: '',
    chapter: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setEventImage(file);
      setEventImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setEventImage(null);
    setEventImagePreview(null);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imageUrl = null;
      
      // Upload image first if present
      if (eventImage) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', eventImage);
        const uploadRes = await axios.post(`${API}/upload/event-image`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.image_url;
      }

      const eventData = {
        ...formData,
        image: imageUrl,
        is_paid: formData.is_paid,
        price: formData.price ? parseFloat(formData.price) : null,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        chapter: formData.chapter || null,
        end_time: formData.end_time || null
      };

      await axios.post(`${API}/events`, eventData);
      setShowCreateDialog(false);
      setFormData({
        title: '',
        description: '',
        date: '',
        end_time: '',
        location: '',
        is_paid: false,
        price: '',
        max_attendees: '',
        chapter: ''
      });
      setEventImage(null);
      setEventImagePreview(null);
      fetchEvents();
      toast.success('Event created!');
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6" data-testid="events-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-bold text-[#2d3748]">
          Events
        </h1>
        {/* Only admins and chapter leaders can create events */}
        {(user?.is_admin || user?.is_area_manager) && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full" data-testid="create-event-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Create New Event</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="rounded-xl bg-gray-50"
                    data-testid="event-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={4}
                    className="rounded-xl bg-gray-50"
                    data-testid="event-description-input"
                  />
                </div>

                {/* Event Image Upload */}
                <div>
                  <Label>Event Image</Label>
                  {eventImagePreview ? (
                    <div className="relative mt-2 inline-block">
                      <img 
                        src={eventImagePreview} 
                        alt="Preview" 
                        className="max-h-40 rounded-xl object-cover"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block mt-2">
                      <div className="flex items-center justify-center gap-2 px-4 py-8 bg-gray-50 hover:bg-gray-100 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-600 transition-colors">
                        <Image className="w-5 h-5" />
                        <span>Click to add event image</span>
                      </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      data-testid="event-image-input"
                    />
                  </label>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Start Date & Time *</Label>
                  <Input
                    id="date"
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="rounded-xl bg-gray-50"
                    data-testid="event-date-input"
                  />
                </div>

                <div>
                  <Label htmlFor="end_time">End Time</Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="rounded-xl bg-gray-50"
                    data-testid="event-end-time-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  className="rounded-xl bg-gray-50"
                  data-testid="event-location-input"
                />
              </div>

              <div>
                <Label>Chapter (optional)</Label>
                <Select value={formData.chapter} onValueChange={(val) => setFormData({ ...formData, chapter: val === 'none' ? '' : val })}>
                  <SelectTrigger className="rounded-xl bg-gray-50" data-testid="event-chapter-select">
                    <SelectValue placeholder="Link to a chapter..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific chapter</SelectItem>
                    {["Durbanville", "Brackenfell and Surrounds", "Helderberg", "Stellenbosch", "Boland", "Southern Peninsula", "Rondebosch and Surrounds", "West Coast Neighbours", "Jozi"].map(ch => (
                      <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="max_attendees">Max Attendees</Label>
                  <Input
                    id="max_attendees"
                    type="number"
                    value={formData.max_attendees}
                    onChange={(e) => setFormData({ ...formData, max_attendees: e.target.value })}
                    className="rounded-xl bg-gray-50"
                    data-testid="event-max-attendees-input"
                  />
                </div>

                <div>
                  <Label htmlFor="price">Price (if paid event)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => {
                      setFormData({ ...formData, price: e.target.value, is_paid: e.target.value > 0 });
                    }}
                    placeholder="e.g. 150 (leave empty for free)"
                    className="rounded-xl bg-gray-50"
                    data-testid="event-price-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full py-3" 
                data-testid="submit-event-btn"
              >
                {loading ? 'Creating...' : 'Create Event'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((event) => (
          <Link key={event.id} to={`/events/${event.id}`}>
            <Card className="border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 h-full overflow-hidden" data-testid="event-card">
              {/* Event Image */}
              {event.image && (
                <div className="aspect-[3/1] overflow-hidden">
                  <img 
                    src={getImageUrl(event.image)} 
                    alt={event.title}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: event.image_position || 'center' }}
                  />
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-xl text-[#2d3748]">
                  {event.title}
                </CardTitle>
                <p className="text-sm text-[#64748b]">by {event.user_name}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-[#64748b]">
                  <Calendar className="w-4 h-4 text-[#5b7f95]" />
                  {format(new Date(event.date), 'PPP p')}
                  {event.end_time && ` — ${event.end_time}`}
                </div>
                <div className="flex items-center gap-2 text-sm text-[#64748b]">
                  <MapPin className="w-4 h-4 text-[#5b7f95]" />
                  {event.location}
                </div>
                <div className="flex items-center gap-2 text-sm text-[#64748b]">
                  <Users className="w-4 h-4 text-[#5b7f95]" />
                  {event.attendees.length} attending
                  {event.max_attendees && ` / ${event.max_attendees} max`}
                </div>
                {event.is_paid && event.price && (
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#cb9944]">
                    <CreditCard className="w-4 h-4" />
                    R{event.price.toFixed(2)}
                  </div>
                )}
                <p className="text-sm text-[#64748b] line-clamp-2">{event.description}</p>
              </CardContent>
            </Card>
          </Link>
        ))}

        {events.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#64748b]">
            <p>No events yet. Create the first one!</p>
          </div>
        )}
      </div>
    </div>
  );
}
